import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { SEALED_MIME, isSealed, openMedia, sealMedia } from "@vmeste/shared";
import type { AttachableType } from "@vmeste/shared";
import { API_BASE, api } from "./api.js";
import { loadSpaceKey } from "./spacekeys.js";
import { syncEngine } from "./sync.js";

/**
 * Files, on the device's side of the envelope (SPEC §3, §9).
 *
 * The crypto is `@vmeste/shared/mediabox` and nothing here reaches past it —
 * this module is the *when*: seal on the way out, open on the way in, and hold
 * the opened bytes for exactly as long as something is looking at them.
 *
 * Three rules shape everything below.
 *
 * **A plaintext space costs nothing.** `useMedia` for a space in `none` mode
 * hands back the URL it was given, in the first render, without a request. The
 * `<img>` fetches it as it always did, with the browser's cache in front.
 *
 * **A file with no envelope opens anyway.** Everything uploaded before a space
 * was locked is still on the server in the clear, under the URL every row
 * already points at. Four bytes say which kind this is (`isSealed`), so both
 * kinds render side by side and the catch-up pass can convert them one at a
 * time without anything going dark in between.
 *
 * **An opened file is eventually revoked.** A blob URL is a reference the
 * browser keeps until the tab closes; a strip of photos scrolled past would
 * otherwise leave every one of them decrypted in memory for the rest of the
 * session. But revoking the moment the last component unmounts made scrolling a
 * conversation download every photograph over and over. So the cache below
 * counts references *and* keeps a bounded set of idle files — see `release`.
 */

/* -------------------------------------------------------------- metadata */

/**
 * Take a photograph's metadata off it, before anything else touches it.
 *
 * A snapshot from a phone carries EXIF: the camera, the moment, and **where it
 * was taken**. Sending that up is the one thing this whole module exists to
 * prevent, and the server cannot help — in an encrypted space it holds octets
 * and could not find a GPS tag if it wanted to. So the cut happens here, on the
 * device, before `sealForUpload` puts the bytes in an envelope.
 *
 * The cut is a redraw. The picture is decoded, painted onto a canvas and read
 * back out; what comes back is a file assembled from pixels, so every tag,
 * thumbnail and colour profile the original carried is simply not in it. That
 * is blunter than editing the EXIF out, and blunt is the point: a parser that
 * knows about `GPSLatitude` does not know about the next vendor's private tag,
 * and a redraw does not need to.
 *
 * **Orientation is the trap.** A phone writes the sensor's pixels down sideways
 * and puts the rotation in EXIF, so dropping the tags naively lays every
 * portrait photograph on its side. `imageOrientation: "from-image"` is what
 * makes `createImageBitmap` apply the rotation while decoding — the pixels come
 * out the way a person saw them, and the tag that used to say so is no longer
 * needed. A browser too old to have `createImageBitmap` gets no redraw at all:
 * a photograph on its side is a fault everybody sees, and refusing to scrub is
 * the smaller of the two.
 */

/**
 * What may be redrawn.
 *
 * Not GIF: a canvas holds one frame, so a redraw would silently turn an
 * animation into a still. Not PDF: it has no pixels in this sense at all.
 * Both go up as they are — neither is a thing a camera writes coordinates into.
 */
const REDRAWABLE = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);

/**
 * Quality for the re-encode, and the one to fall back to.
 *
 * 0.9 is chosen rather than 1.0 on purpose. A camera writes JPEG at roughly
 * 0.95 with a full-size preview stapled on; re-encoding at 0.9 lands under the
 * original almost every time and the difference is not visible at any size a
 * phone shows. Asking for 1.0 would produce a file *larger* than the one picked
 * — a worse photograph and a longer upload, for nothing.
 *
 * The thrifty pass exists only for the case below, where the alternative to a
 * harder squeeze is keeping the coordinates.
 */
const QUALITY = 0.9;
const THRIFTY_QUALITY = 0.72;

/**
 * The most pixels to put on a canvas.
 *
 * Not a feature — a floor under a real failure. iOS caps a canvas at about
 * 16.7 megapixels and hands back a blank rectangle rather than an error above
 * it, which would mean uploading a white square in place of somebody's
 * photograph. A default phone camera is 12 MP and passes through untouched;
 * a 48 MP shot is scaled to fit and is still far larger than any screen it will
 * be looked at on.
 */
const MAX_PIXELS = 16_000_000;

const EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/avif": "avif",
};

/**
 * Encode the canvas, or answer `null` when the browser cannot.
 *
 * The check on `blob.type` is not paranoia: `toBlob` given a format it cannot
 * write does not fail, it quietly returns a PNG. No browser encodes AVIF today,
 * so without this an AVIF photograph would come back as a PNG several times its
 * size and be sent that way.
 */
function encode(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob && blob.type === type ? blob : null), type, quality);
  });
}

/** Keep the name, correct the extension, so a saved file still opens by name. */
function renamed(name: string, mime: string): string {
  const extension = EXTENSIONS[mime];
  if (!extension) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem}.${extension}`;
}

/**
 * The same picture with nothing but the picture in it.
 *
 * Hands back the file it was given whenever it cannot do better: an unsupported
 * type, a browser without `createImageBitmap`, an encoder that refused, or a
 * result no smaller than what was picked. Never throws — a caller's alternative
 * to a scrubbed file is the file itself, not a failed upload.
 */
export async function withoutMetadata(file: File): Promise<File> {
  const mime = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (!REDRAWABLE.has(mime)) return file;
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Bytes the decoder would not take. The server's own check is the one that
    // gets to refuse them; this is not the place to decide the file is broken.
    return file;
  }

  try {
    const scale = Math.min(1, Math.sqrt(MAX_PIXELS / (bitmap.width * bitmap.height)));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);

    // Its own format first, so a picture with transparency keeps it and a
    // photograph is not turned into something else for no reason. WebP is the
    // stand-in when the browser cannot write that format — it is on the
    // server's allow-list, it keeps an alpha channel, and everything current
    // encodes it.
    let redrawn = (await encode(canvas, mime, QUALITY)) ?? (await encode(canvas, "image/webp", QUALITY));

    /*
     * Bigger than the original, which happens: a PNG redrawn as a PNG, or a
     * photograph already compressed harder than this asks for. One more pass at
     * a lower quality, because the thing being traded away is not bytes — it is
     * the coordinates of somebody's home. PNG is skipped: it ignores quality,
     * so a second pass would return the same file.
     */
    if (redrawn && redrawn.size >= file.size && redrawn.type !== "image/png") {
      redrawn = (await encode(canvas, redrawn.type, THRIFTY_QUALITY)) ?? redrawn;
    }

    /*
     * Still bigger, so the original goes — **metadata and all**.
     *
     * Said plainly because it is a real hole: a file this happens to keeps its
     * EXIF. It is the lesser wrong. Sending a visibly worse and heavier copy
     * over a phone's uplink is a cost on every upload of that kind, while the
     * photographs that actually carry coordinates are camera JPEGs, and those
     * come out smaller on the first pass every time. What lands here instead is
     * screenshots and exported PNGs — pictures with no camera behind them.
     */
    if (!redrawn || redrawn.size >= file.size) return file;

    return new File([redrawn], renamed(file.name, redrawn.type), { type: redrawn.type });
  } finally {
    bitmap.close();
  }
}

/* ---------------------------------------------------------------- outgoing */

/** What a sealed part is called in the multipart body. See `sealForUpload`. */
const NEUTRAL_NAME = "file.bin";

/**
 * The file to actually send for a file a person picked.
 *
 * In a plaintext space, the same one. In an encrypted one, the envelope — and
 * the part is renamed on the way: a filename in a multipart body is a filename
 * on the server's disk, which is the leak the content blob exists to close.
 * `holiday-scan.pdf` says as much as the file does. The real name travels
 * encrypted in the attachment's `content`, and the real type inside the
 * envelope, so what goes up is `file.bin` full of octets.
 *
 * Throws when the space is encrypted and this device has no key: sending the
 * bytes in the clear instead would be the one failure this whole path exists to
 * prevent, and doing it quietly is worse than refusing.
 */
export async function sealForUpload(spaceId: string, file: File): Promise<File> {
  const codec = syncEngine.codec(spaceId);
  if (!codec) throw new Error("this space is locked on this device");
  if (codec.mode === "none") return file;

  const key = await loadSpaceKey(spaceId);
  if (!key) throw new Error("this device has no key for this space");

  const mime = file.type.split(";")[0]?.trim().toLowerCase() ?? "";
  const sealed = await sealMedia(key, mime, new Uint8Array(await file.arrayBuffer()));

  return new File([sealed as BlobPart], NEUTRAL_NAME, { type: SEALED_MIME });
}

/* ------------------------------------------------------------- waiting out */

/**
 * Where a file picked with no network waits for one.
 *
 * IndexedDB, for the reason the outbox is *not* the answer here: that queue is
 * a `localStorage` string and a 10 MB photograph in it would blow the quota and
 * take every unsent list change down with it. IndexedDB stores a `Blob` as a
 * `Blob` — no base64, no string, no five-megabyte-per-origin ceiling — so the
 * bytes can wait without standing on anything else's toes.
 *
 * Same shape as `spacekeys.ts`, deliberately: one database per concern, opened
 * per operation and closed after it. A separate database rather than a store
 * inside that one, because signing out clears the keys and these are not keys —
 * see `forgetQueuedUploads`.
 *
 * **The file waits unsealed.** Sealing it on the way in would be encryption at
 * rest and was the first instinct, but the key can be rotated while a file
 * waits — the re-encryption pass exists — and an envelope under a retired key
 * is bytes nobody will ever open again. What is stored instead is a second copy
 * of a photograph that is already sitting unencrypted in the same phone's photo
 * library. Sealing happens at the moment of sending, with whatever key the
 * space has then.
 */

const QUEUE_DB = "vmeste-uploads";
const QUEUE_VERSION = 1;
const QUEUE_STORE = "queued";

/** A file that has not gone up yet, with everything needed to send it later. */
export interface QueuedUpload {
  /**
   * The id the attachment will be created under — and the id of the row the
   * strip is already drawing for it. The same on every retry, so a request that
   * arrived but whose answer was lost cannot become a second copy.
   */
  id: string;
  spaceId: string;
  targetType: AttachableType;
  targetId: string;
  /** The name as the person will see it, already trimmed to what the row holds. */
  name: string;
  /** As picked and scrubbed. Sealed when it is sent, not when it is stored. */
  file: File;
  /** Transmissions that failed. Drives the backoff and the give-up threshold. */
  attempts: number;
  queuedAt: number;
}

function openQueue(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is unavailable"));
      return;
    }

    const request = indexedDB.open(QUEUE_DB, QUEUE_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE);
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("could not open the upload queue"));
  });
}

function runQueue<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return openQueue().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(QUEUE_STORE, mode);
        const request = work(tx.objectStore(QUEUE_STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error("upload queue failed"));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * Keep this file until there is a network. `false` when it could not be kept.
 *
 * The disk is finite and a person can pick a dozen photographs on a train, so
 * the quota is a refusal this has to be able to report: a caller told `false`
 * says so on the tile rather than promising to send something it has dropped.
 */
export async function queueUpload(entry: QueuedUpload): Promise<boolean> {
  try {
    await runQueue("readwrite", (store) => store.put(entry, entry.id));
    return true;
  } catch {
    // Out of quota, private browsing, storage denied. All the same answer.
    return false;
  }
}

/** Everything waiting, oldest first — the order the files were picked in. */
/** One queued file by its id, or `null` if it is no longer waiting. */
export async function queuedUpload(id: string): Promise<QueuedUpload | null> {
  try {
    return (await runQueue<QueuedUpload | undefined>("readonly", (store) => store.get(id))) ?? null;
  } catch {
    return null;
  }
}

export async function queuedUploads(): Promise<QueuedUpload[]> {
  try {
    const rows = await runQueue<QueuedUpload[]>("readonly", (store) => store.getAll());
    return [...rows].sort((a, b) => a.queuedAt - b.queuedAt);
  } catch {
    return [];
  }
}

export async function dropQueuedUpload(id: string): Promise<void> {
  try {
    await runQueue("readwrite", (store) => store.delete(id));
  } catch {
    /* nowhere to drop it from — the entry is unreachable either way */
  }
}

/**
 * Throw the whole queue away.
 *
 * For signing out. A photograph left on a shared computer after the person who
 * picked it has left is the same failure a forgotten key is, and the bytes here
 * are in the clear.
 */
export async function forgetQueuedUploads(): Promise<void> {
  try {
    await runQueue("readwrite", (store) => store.clear());
  } catch {
    /* nothing to forget, or nowhere to forget it from */
  }
}

/* ---------------------------------------------------------------- incoming */

interface Downloaded {
  bytes: Uint8Array;
  /** What the server says it is. Meaningless for an envelope, which says so. */
  mime: string;
}

/**
 * The bytes behind a media URL, with the session cookie `lib/api` relies on.
 *
 * `fresh` skips the browser cache, and only the catch-up pass asks for it: the
 * media route serves `immutable` because an id is normally the hash of its own
 * content, and sealing in place is the one thing that breaks that promise. A
 * cached plaintext copy is fine to *show* — it is the same picture — but the
 * pass has to see what is actually on the disk now.
 */
async function download(
  url: string,
  options: { signal?: AbortSignal; fresh?: boolean } = {},
): Promise<Downloaded> {
  const response = await fetch(url, {
    // The session is an httpOnly cookie (SPEC §8) and the media route is on our
    // own origin, so this is the same credential every other request carries.
    credentials: "same-origin",
    ...(options.fresh ? { cache: "reload" as RequestCache } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (!response.ok) throw new Error(`media ${response.status}`);

  const declared = response.headers.get("content-type") ?? "";

  return {
    bytes: new Uint8Array(await response.arrayBuffer()),
    mime: declared.split(";")[0]?.trim().toLowerCase() || SEALED_MIME,
  };
}

/** A file resolved to something renderable. */
interface Opened {
  url: string;
  /** The type from inside the envelope, or `null` for a file that had none. */
  mime: string | null;
  /** True when `url` is ours to revoke. */
  minted: boolean;
  /** Decrypted size, for the cache's bound. Nought for a URL we did not mint. */
  size: number;
}

async function openFile(
  spaceId: string,
  url: string,
  signal: AbortSignal,
): Promise<Opened> {
  const { bytes } = await download(url, { signal });

  // Uploaded before the space was locked. The server's own `Content-Type` is
  // the true one there, so the original URL is handed straight back and the
  // browser's cache goes on serving it as it always did.
  if (!isSealed(bytes)) return { url, mime: null, minted: false, size: 0 };

  const key = await loadSpaceKey(spaceId);
  if (!key) throw new Error("this device has no key for this space");

  const opened = await openMedia(key, bytes);

  return {
    url: URL.createObjectURL(new Blob([opened.bytes as BlobPart], { type: opened.mime })),
    mime: opened.mime,
    minted: true,
    size: opened.bytes.byteLength,
  };
}

/* ------------------------------------------------------------------- cache */

/**
 * One download per URL, however many components ask for it.
 *
 * A photo under a chat message is rendered by the strip in the bubble and again
 * in whatever opened it; a wish's picture is on the card and in the sheet. Two
 * components, one 4 MB file, one decryption.
 *
 * The counting is the whole point: revoking while somebody is still showing the
 * blob pulls the picture out from under them. But counting alone was too eager
 * — a file nobody is looking at *right now* is not a file nobody wants, and
 * dropping it on unmount made a chat re-download every photograph on every
 * scroll. Zero refs means idle, not dead; see `idle` below for what ends it.
 */
interface Entry {
  refs: number;
  /** Set only for a blob URL this module made, which is the only kind to revoke. */
  objectUrl: string | null;
  aborter: AbortController;
  work: Promise<Opened | null>;
  /** Bytes held, once they are known. Nought until the file has been opened. */
  bytes: number;
}

const cache = new Map<string, Entry>();

/**
 * Pictures nobody is looking at right now, newest last.
 *
 * They used to be thrown away the moment the last consumer left, which on a
 * chat means the moment a photograph scrolls off the screen — so scrolling back
 * downloaded and decrypted it all over again, and every message showed its
 * picture arriving a second time. That is the "loading again" this exists to
 * stop.
 *
 * Kept, but not indefinitely: a decrypted photograph is held in the tab's
 * memory, and a conversation with a hundred of them would be a hundred
 * photographs in memory. The bound is bytes rather than count, because that is
 * the thing that actually costs — and it is deliberately generous enough to
 * hold a screenful and a scroll either side of it.
 */
const idle = new Set<string>();

const IDLE_MAX_BYTES = 64 * 1024 * 1024;
const IDLE_MAX_ENTRIES = 32;

function idleBytes(): number {
  let total = 0;
  for (const key of idle) total += cache.get(key)?.bytes ?? 0;
  return total;
}

/** Drop the least recently let go of, until the cache is inside its bounds. */
function evict(): void {
  while (idle.size > IDLE_MAX_ENTRIES || (idle.size > 0 && idleBytes() > IDLE_MAX_BYTES)) {
    const oldest = idle.values().next().value;
    if (oldest === undefined) return;
    idle.delete(oldest);
    const entry = cache.get(oldest);
    if (entry) discard(oldest, entry);
  }
}

const cacheKey = (spaceId: string, url: string): string => `${spaceId}|${url}`;

function discard(key: string, entry: Entry): void {
  if (cache.get(key) === entry) cache.delete(key);
  idle.delete(key);
  entry.aborter.abort();
  if (entry.objectUrl) {
    URL.revokeObjectURL(entry.objectUrl);
    entry.objectUrl = null;
  }
}

function acquire(key: string, spaceId: string, url: string): Entry {
  const existing = cache.get(key);
  if (existing) {
    // Taken up again: out of the idle set, and back to the end of it when it is
    // next let go, so what is being looked at is the last thing evicted.
    idle.delete(key);
    existing.refs += 1;
    return existing;
  }

  const aborter = new AbortController();
  const entry: Entry = {
    refs: 1,
    objectUrl: null,
    aborter,
    work: Promise.resolve(null),
    bytes: 0,
  };

  entry.work = openFile(spaceId, url, aborter.signal)
    .then((opened) => {
      if (opened.minted) entry.objectUrl = opened.url;
      entry.bytes = opened.size;

      // Everybody who wanted this left while it was on the wire. The blob was
      // minted after `discard` ran, so it has to be revoked here or it is
      // unreachable and permanent.
      if (cache.get(key) !== entry) {
        if (entry.objectUrl) {
          URL.revokeObjectURL(entry.objectUrl);
          entry.objectUrl = null;
        }
        return null;
      }

      // Its weight is only known now, and it may be sitting in the idle set
      // already — opened, then scrolled past before it had finished arriving.
      if (entry.refs === 0) evict();

      return opened;
    })
    // No key, the wrong key, altered bytes, a 404, an aborted fetch. Nothing
    // here can tell those apart and none of them has a different answer.
    .catch(() => null);

  cache.set(key, entry);
  return entry;
}

/**
 * Throw away every decrypted file this tab is holding.
 *
 * Signing out, and nothing else. The keys go at the same moment (see
 * `session.tsx`), and leaving decrypted photographs in memory beside a
 * deliberately forgotten key would undo the point of the gesture — the more so
 * now that idle files outlive the components that showed them.
 */
export function forgetMediaCache(): void {
  for (const [key, entry] of [...cache]) discard(key, entry);
  idle.clear();
}

function release(key: string): void {
  const entry = cache.get(key);
  if (!entry) return;

  entry.refs -= 1;
  if (entry.refs > 0) return;

  /*
   * Kept rather than thrown away.
   *
   * Nothing is looking at it now, but a photograph that scrolled off the top
   * of a conversation is the one most likely to be wanted next — and the cost
   * of being wrong is downloading and decrypting it again in front of somebody
   * who is only scrolling. It leaves when the cache is over its bounds, oldest
   * first, and it also covers the StrictMode double-mount and the swap of one
   * component for another wanting the same file, which used to need a timer.
   */
  idle.delete(key);
  idle.add(key);
  evict();
}

/* -------------------------------------------------------------------- hook */

export type MediaStatus = "loading" | "ready" | "failed";

export interface Media {
  /** Ready for `src`/`href`. `null` while loading, and after a failure. */
  url: string | null;
  /**
   * The type from inside the envelope, once it is known.
   *
   * `null` for a file served as it was stored — there the row's own `mime` is
   * the true one. In an encrypted space the server's copy says
   * `application/octet-stream` and means it, so this is the only thing that can
   * tell a photograph from a document.
   */
  mime: string | null;
  status: MediaStatus;
}

const PENDING: Media = { url: null, mime: null, status: "loading" };
const BROKEN: Media = { url: null, mime: null, status: "failed" };

/**
 * A media URL, opened if it needs opening.
 *
 * `null` covers both "not yet" and "not ever", deliberately: a caller reserving
 * the space wants the same square either way, and `status` says which it is for
 * the one place that shows a different thing.
 */
export function useMedia(spaceId: string | null, url: string): Media {
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  // Only the mode matters here, and it comes from the space's own row rather
  // than from the codec: a locked space has no codec at all, and that is a file
  // that fails to open, not a file to hand over in the clear.
  const sealedSpace = useMemo(
    () => (spaceId ? syncEngine.view(spaceId).space?.encryptionMode === "e2ee" : false),
    [spaceId, snapshot],
  );

  const key = spaceId && sealedSpace ? cacheKey(spaceId, url) : null;
  const [resolved, setResolved] = useState<{ key: string; opened: Opened | null } | null>(
    null,
  );

  useEffect(() => {
    if (!key || !spaceId) return;

    let live = true;
    const entry = acquire(key, spaceId, url);
    void entry.work.then((opened) => {
      if (live) setResolved({ key, opened });
    });

    return () => {
      live = false;
      release(key);
    };
  }, [key, spaceId, url]);

  if (!key) return { url, mime: null, status: "ready" };

  // Keyed by the URL it answers: after a change the previous file's blob is
  // already being revoked, and rendering it for one frame is a broken image.
  if (!resolved || resolved.key !== key) return PENDING;

  return resolved.opened
    ? { url: resolved.opened.url, mime: resolved.opened.mime, status: "ready" }
    : BROKEN;
}

/** The URL alone, for `<img src>` and `<a href>`. `null` until it is usable. */
export function useMediaUrl(spaceId: string | null, url: string): string | null {
  return useMedia(spaceId, url).url;
}

/* -------------------------------------------------------------- catching up */

export interface MediaSealProgress {
  /** Files still in the clear when the pass started. */
  total: number;
  /** Files sealed so far. */
  done: number;
}

/**
 * Seal the files uploaded before the space was locked.
 *
 * The same shape and the same resumability as `reencryptSpace`, and for the
 * same reason: "still in the clear" is a property of the files themselves — the
 * magic at the front of each one — rather than a cursor kept anywhere, so a
 * pass that dies halfway is finished simply by running it again, from this
 * device or the other one. Being interrupted is the ordinary case, not the
 * failure case: this walks every photograph two people have exchanged, over a
 * phone connection, and nothing is left broken in between.
 *
 * One file at a time, sent as soon as it is sealed. A batch held in memory
 * until the whole of it was ready would be a batch whose progress nobody can
 * account for when it fails, and ten megabytes each is not a thing to hold ten
 * of.
 */
export async function sealSpaceMedia(
  spaceId: string,
  onProgress?: (progress: MediaSealProgress) => void,
): Promise<MediaSealProgress> {
  const codec = syncEngine.codec(spaceId);
  const key = await loadSpaceKey(spaceId);
  if (!codec || codec.mode !== "e2ee" || !key) {
    throw new Error("this space is not encrypted on this device");
  }

  let total = 0;
  let done = 0;
  let previous = "";
  onProgress?.({ total, done });

  for (;;) {
    // Asked again each round rather than walked once: another device may be
    // running the same pass, and a file sealed there is one this one is spared.
    const { mediaIds, more } = await api.unsealedMedia(spaceId);
    if (mediaIds.length === 0) break;

    /*
     * The very same batch as last time.
     *
     * Every file in it was sent and the server still calls them unsealed, so it
     * disagrees with this device about what is done and another round would
     * fetch the same files for ever. Stopping leaves the pass unfinished, which
     * this design already handles. A spin loop is not.
     */
    const signature = mediaIds.join("|");
    if (signature === previous) break;
    previous = signature;

    /*
     * The total is only ever known one batch at a time — the listing is capped
     * — so it grows rather than being counted up front. `more` adds one to it
     * so that a bar built on this cannot read "finished" while the server is
     * still holding another batch back.
     */
    total = Math.max(total, done + mediaIds.length + (more ? 1 : 0));
    onProgress?.({ total, done });

    for (const id of mediaIds) {
      const url = `${API_BASE}/media/${encodeURIComponent(id)}`;
      const file = await download(url, { fresh: true });

      // Sealed since the listing was drawn up — the other device got there
      // first. Progress all the same: this file is done.
      if (!isSealed(file.bytes)) {
        const envelope = await sealMedia(key, file.mime, file.bytes);
        await api.sealMedia(
          id,
          new File([envelope as BlobPart], NEUTRAL_NAME, { type: SEALED_MIME }),
        );
      }

      done += 1;
      onProgress?.({ total: Math.max(total, done), done });
    }
  }

  return { total: Math.max(total, done), done };
}
